import { Center, Flex, Group, Loader, Paper, Text } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useClients } from 'src/clients/hooks/useClients';
import { useFamily } from 'src/clients/hooks/useFamily';
import UnderlineTabs from 'src/UI/page-nav/UnderlineTabs';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { NameFormat, formatName } from 'src/utils/nameFormatting';
import FamilyFileUploadPage from 'src/data-capture/file-management/family/FamilyFileUploadPage';
import { FamilyIndividualToggle } from '../FamilyIndividualToggle';
import { SquareIconButton } from '../SquareIconButton';
import { FamilyMember, buildFamilyMembers } from './useFamilyMembers';
import { FamilyProfilePanel } from './FamilyProfileContent';
import { FamilyFinancialPanel } from './FamilyFinancialPanel';
import { FamilyMeetingsPanel } from './FamilyMeetingsPanel';
import { FamilyCashflowPanel } from './FamilyCashflowPanel';
import { individualTabForFamily } from './tabMapping';

const FAMILY_TABS = ['profile', 'financial', 'cashflows', 'documents', 'meetings'] as const;
type FamilyTab = (typeof FAMILY_TABS)[number];

export function FamilyData() {
  const {
    familyId = '',
    tab,
    sectionSlug,
  } = useParams<{
    familyId: string;
    tab?: string;
    sectionSlug?: string;
  }>();
  const { family, isLoading: isFamilyLoading } = useFamily(familyId);
  const { clients, isLoading: isClientsLoading } = useClients(
    { familyId },
    { enabled: !!familyId },
  );
  const {
    flagEnabledFinancialRework,
    flagEnabledFamilyMeetingsRework,
    flagEnabledFamilyCashflowsRevamp,
    flagEnabledFamilyDocumentsRevamp,
  } = useFlags();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const activeTab: FamilyTab = FAMILY_TABS.includes(tab as FamilyTab)
    ? (tab as FamilyTab)
    : 'profile';

  const mainMemberClientId = family?.mainFamilyMember.clientId;
  const memberParam = searchParams.get('member');

  const clientById = Object.fromEntries(clients.map(c => [c.id, c]));

  const members: FamilyMember[] = buildFamilyMembers(family, clientById);

  // Trust the ?member param as the came-from client: it is set by our own
  // navigation. Validating it against the family-filtered clients query would
  // fall back to the main member while that query is still loading (the family
  // is often cached and resolves first), losing the client the user came from.
  // Cold deep-links without ?member default to the main family member.
  const contextClientId = memberParam ?? mainMemberClientId ?? null;

  const contextClient = contextClientId ? clientById[contextClientId] : null;
  const contextClientName = contextClient
    ? formatName(contextClient.firstName, contextClient.lastName, NameFormat.NATURAL)
    : 'Individual';

  const familyLabel = family?.name ?? 'Family';

  const hasOtherMembers = members.some(m => m.clientId !== contextClientId);

  function handleTabChange(nextTab: string) {
    const query = searchParams.toString();
    navigate(`/family/${familyId}/${nextTab}${query ? `?${query}` : ''}`);
  }

  function handleToggleChange(value: 'family' | 'individual') {
    if (value === 'individual' && contextClientId) {
      navigate(`/client/${contextClientId}/${individualTabForFamily(activeTab)}`);
    }
  }

  function handleMemberSelect(memberId: string) {
    navigate(`/client/${memberId}/${individualTabForFamily(activeTab)}`);
  }

  const toggle = (
    <FamilyIndividualToggle
      value="family"
      onChange={handleToggleChange}
      familyLabel={familyLabel}
      individualLabel={contextClientName}
      members={hasOtherMembers ? members : undefined}
      onMemberSelect={hasOtherMembers ? handleMemberSelect : undefined}
    />
  );

  const tabs = [
    { label: 'Profile', value: 'profile' },
    { label: 'Financial', value: 'financial' },
    { label: 'Cashflows', value: 'cashflows' },
    { label: 'Documents', value: 'documents' },
    { label: 'Meetings', value: 'meetings' },
  ];

  const backButton = (
    <SquareIconButton
      diameter={48}
      radius={20}
      aria-label="Back to families"
      onClick={() => navigate('/clients/families')}
    >
      <IconArrowLeft size={24} color="var(--mantine-color-gray-9)" />
    </SquareIconButton>
  );

  if (isFamilyLoading || isClientsLoading) {
    return (
      <Flex direction="column" gap="lg" pos="relative">
        <UnderlineTabs
          mode="local"
          routerTabsV2
          tabs={tabs}
          value={activeTab}
          onChange={handleTabChange}
          leadingContent={
            <Group gap="sm" wrap="nowrap">
              {backButton}
            </Group>
          }
        />
        <Paper variant="content">
          <Center py="xxl">
            <Loader />
          </Center>
        </Paper>
      </Flex>
    );
  }

  if (!family) {
    return (
      <Flex direction="column" gap="lg" pos="relative">
        <Group gap="sm" wrap="nowrap">
          {backButton}
        </Group>
        <Paper variant="content">
          <Text c="gray.6">This family could not be found.</Text>
        </Paper>
      </Flex>
    );
  }

  let content: React.ReactNode;
  if (activeTab === 'profile') {
    content = <FamilyProfilePanel familyId={familyId} />;
  } else if (activeTab === 'financial' && flagEnabledFinancialRework) {
    content = <FamilyFinancialPanel members={members} />;
  } else if (activeTab === 'cashflows' && flagEnabledFamilyCashflowsRevamp) {
    content = <FamilyCashflowPanel members={members} />;
  } else if (activeTab === 'documents' && flagEnabledFamilyDocumentsRevamp) {
    content = <FamilyFileUploadPage />;
  } else if (activeTab === 'meetings' && flagEnabledFamilyMeetingsRework) {
    content = <FamilyMeetingsPanel familyId={familyId} section={sectionSlug} />;
  } else {
    content = (
      <Paper variant="content">
        <Center py="xxl">
          <Text c="gray.6">Coming soon</Text>
        </Center>
      </Paper>
    );
  }

  return (
    <Flex direction="column" gap="lg" pos="relative">
      <UnderlineTabs
        mode="local"
        routerTabsV2
        tabs={tabs}
        value={activeTab}
        onChange={handleTabChange}
        leadingContent={
          <Group gap="sm" wrap="nowrap">
            {backButton}
            {toggle}
          </Group>
        }
      />
      {content}
    </Flex>
  );
}
